// Live-verified strings come from docs/spec/modules/time-off.md (reference screenshots and the live-verified
// section); the rest follow the spec's (unverified) wording.
export const s = {
  pageTitle: "Time off",
  requestTimeOff: "Request time off",
  settingsMenu: "Time off settings",
  managePolicies: "Manage policies",
  manageHolidays: "Manage holidays",
  tabRequests: "Requests",
  tabTimeline: "Timeline",
  tabBalance: "Balance",
  backToTimeOff: "Time off",

  // turned-off card (spec 2)
  offEyebrow: "TIME OFF",
  offTitle: "Manage leaves & holidays",
  offBullets: [
    "Create holidays and time off policies",
    "Request leave and time off",
    "See who has time off on a timeline",
    "See accrued, used, and available balance",
  ],
  enable: "Enable",
  askAdmin: "Ask your workspace admin to turn on Time off.",

  // requests tab
  showAll: "Show all time off",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  team: "Team",
  requestsCaption: "Requests",
  export: "Export",
  saveCsv: "Save as CSV",
  colTeamMember: "Team member",
  colPeriod: "Period",
  colRequested: "Requested",
  colPolicy: "Policy",
  colStatus: "Status",
  colNote: "Note",
  approve: "Approve",
  reject: "Reject",
  withdraw: "Withdraw",
  edit: "Edit",
  actions: "Actions",
  requestedOn: (d: string) => `Requested on ${d}`,
  ofAvailable: (v: string) => `of ${v}`,
  firstHalf: "(first half)",
  secondHalf: "(second half)",
  statusBy: (verb: string, who: string, when: string) => `${verb} by ${who} on ${when}`,
  noRequests: "No time off requests",
  noPoliciesHint: "Create a policy to start requesting time off.",
  allDates: "All dates",
  rejectTitle: "Reject",
  rejectReason: "Reason for rejection",
  withdrawTitle: "Withdraw request?",
  withdrawMessage: "The requested amount will be returned to the balance.",
  approvedToast: "Time off approved",
  rejectedToast: "Time off rejected",
  withdrawnToast: "Time off withdrawn",
  requestedToast: "Time off requested",
  updatedToast: "Request updated",

  // request modal
  editRequest: "Edit request",
  member: "Member",
  policy: "Time off policy",
  selectPolicy: "Select policy",
  available: "Available:",
  dateRange: "Date range",
  dateRangeInfo: "Holidays and non-working days are not counted",
  dayPeriod: "Day period",
  fullDay: "Full day",
  firstHalfOpt: "First half",
  secondHalfOpt: "Second half",
  from: "From",
  to: "To",
  total: "Total:",
  note: "Note",
  addNote: "Add a note",
  byManager: "Request made by manager",
  submit: "Submit",
  cancel: "Cancel",
  save: "Save",
  close: "Close",

  // timeline
  timelineMember: "Team member",
  previous: "Previous period",
  next: "Next period",

  // balance
  groupBy: "Group by",
  byPolicy: "Policy",
  byUser: "User",
  balanceCaption: "Balance",
  colAccrued: "Accrued",
  colUsed: "Used",
  colAvailable: "Available",
  negativeSuffix: "negative",
  ofLimit: (v: string) => `(of ${v})`,
  manageBalance: "Manage balance",
  balanceDetails: "Balance details",
  history: "History",
  addToBalance: "Add to balance",
  add: "Add",
  remove: "Remove",
  balance: "Balance",
  reasonForAdjusting: "Reason for adjusting",
  validFrom: "Valid from",
  validUntil: "Valid until",
  accruedLabel: "ACCRUED:",
  negativeAllowed: (v: string) => `Negative allowed: ${v}`,
  usedLabel: (v: string) => `Used: ${v}`,
  validityPeriod: "Validity period",
  noExpiry: "(no expiry date)",
  balanceHistory: "Balance history",
  colDate: "Date",
  colAuthor: "Author",
  colChanges: "Changes",
  colAction: "Action",
  withoutNote: "(Without note)",
  system: "System",
  noBalances: "No balances",
  membersSelected: (n: number, policy: string) => `${n} members selected · ${policy}`,
  balanceSaved: "Balance updated",
  archivedSuffix: "(archived)",
  actionLabels: {
    MANUAL: "Manual",
    PENDING: "Pending",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    WITHDRAWN: "Withdrawn",
    ACCRUAL: "Accrual",
    EXPIRED: "Expired",
    NEGATIVE_RESET: "Negative reset",
    TOIL: "TOIL",
    REPRICED: "Edited",
  } as Record<string, string>,
  unitDays: "days",
  unitHours: "hours",

  // policies
  policiesCaption: "Policies",
  showActive: "Show active",
  showArchived: "Show archived",
  showAllPolicies: "Show all",
  search: "Search",
  createPolicy: "Create new policy",
  colName: "Name",
  colUnit: "Unit",
  colAccrual: "Accrual",
  colAssignees: "Assignees",
  archive: "Archive",
  restore: "Restore",
  delete: "Delete",
  everyone: "Everyone",
  membersChip: (n: number) => `${n} ${n === 1 ? "member" : "members"}`,
  groupsChip: (n: number) => `${n} ${n === 1 ? "group" : "groups"}`,
  perPeriod: (amount: number, unit: string, period: string) => `${amount} ${unit} / ${period}`,
  archivePolicyTitle: "Archive policy?",
  archivePolicyMessage: "Members won't be able to request time off under this policy. Balances and requests are kept.",
  deletePolicyTitle: "Delete policy",
  deletePolicyMessage:
    "All requests, balances and history of this policy will be permanently deleted. Type DELETE to confirm.",
  typeDelete: "DELETE",
  noPolicies: "No policies",

  // policy modal
  createPolicyTitle: "Create policy",
  editPolicyTitle: "Edit policy",
  create: "Create",
  policyName: "Policy name",
  iconColor: "Icon and color",
  assignees: "Assignees",
  selectAssignees: "Select members",
  timeUnit: "Time unit",
  days: "Days",
  hours: "Hours",
  requiresApproval: "Requires approval",
  requiresApprovalInfo: "If unchecked, all requests are automatically approved",
  whoCanApprove: "Who can approve:",
  admins: "Admins",
  teamManagers: "Team managers",
  specificMembers: "Specific members",
  selectApprovers: "Select members who can approve",
  balanceExpiry: "Balance expiry",
  neverExpires: "Never expires",
  hasExpiryDates: "Has expiry dates",
  autoAccrues: "Automatically accrues",
  every: "every",
  year: "year",
  month: "month",
  allowNegative: "Allow negative balance",
  limitTo: "Limit to",
  per: "per",
  resetNegative: (period: string) => `Reset negative balance at the end of the ${period}`,
  allowHalfDay: "Allow half day",
  autoEntries: "Automatically create time entries",
  projectId: "Project",
  projectPlaceholder: "Project ID",
  policySaved: "Policy saved",

  // holidays
  holidaysCaption: "Holidays",
  createHoliday: "Create new holiday",
  importHolidays: "Import public holidays",
  colDateRange: "Date",
  occursAnnually: "Occurs annually",
  createHolidayTitle: "Create holiday",
  editHolidayTitle: "Edit holiday",
  holidayName: "Name",
  period: "Period",
  deleteHolidayTitle: "Delete holiday",
  deleteHolidayMessage: "Are you sure you want to delete this holiday?",
  noHolidays: "No holidays yet",
  country: "Country",
  yearLabel: "Year",
  autoImportYearly: "Auto-import every year",
  importAction: "Import holidays",
  imported: (n: number) => `${n} holidays imported`,
  holidaySaved: "Holiday saved",
  color: "Color",
} as const;
